import React, {
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { INTERIOR_RESOURCES } from '../../assetsDictionary';
import videoFragment from "./videoFragment.glsl";
import staticVertex from "../staticVertex.glsl";
import AudioLooper from "../../components/AudioLooper";

const VideoSphere = forwardRef(function VideoSphere(
  { src, loopSrc, radius, rotation, videoAlpha = 0.0 },
  ref
) {
    const matRef = useRef(null);
    const { gl } = useThree();

    const mainVideoRef = useRef(null);
    const loopVideoRef = useRef(null);

    const mainTexRef = useRef(null);
    const loopTexRef = useRef(null);

    const audioRef = useRef(null);
    const fadeIntervalRef = useRef(null);
    const audioContextRef = useRef(null);
    const audioBufferRef = useRef(null);
    const audioSourceRef = useRef(null);
    const gainNodeRef = useRef(null);


  useEffect(() => {
    // --- video 1 (despegue) ---
    const v1 = document.createElement("video");
    v1.src = src;
    v1.crossOrigin = "anonymous";
    v1.loop = false;
    v1.muted = true;
    v1.playsInline = true;
    v1.preload = "auto";
    v1.autoplay = false;
    v1.pause();
    v1.currentTime = 0;

    const t1 = new THREE.VideoTexture(v1);
    t1.colorSpace = THREE.SRGBColorSpace;
    t1.minFilter = THREE.LinearFilter;
    t1.magFilter = THREE.LinearFilter;
    t1.generateMipmaps = false;
    // t1.anisotropy = gl.capabilities.getMaxAnisotropy();
    t1.anisotropy = Math.min(8, gl.capabilities.getMaxAnisotropy());

    mainVideoRef.current = v1;
    mainTexRef.current = t1;

    // --- video 2 (nubes) precargado ---
    let v2 = null;
    let t2 = null;

    if (loopSrc) {
      v2 = document.createElement("video");
      v2.src = loopSrc;
      v2.crossOrigin = "anonymous";
      v2.loop = true;
      v2.muted = true;
      v2.playsInline = true;
      v2.preload = "auto";
      v2.autoplay = false;
      v2.pause();
      v2.currentTime = 0;

      t2 = new THREE.VideoTexture(v2);
      t2.colorSpace = THREE.SRGBColorSpace;
      t2.minFilter = THREE.LinearFilter;
      t2.magFilter = THREE.LinearFilter;
      t2.generateMipmaps = false;
      t2.anisotropy = gl.capabilities.getMaxAnisotropy();

      loopVideoRef.current = v2;
      loopTexRef.current = t2;
    }

    // setear textura inicial al material (cuando exista)
    if (matRef.current) {
      matRef.current.uniforms.uTexture.value = t1;
      matRef.current.uniforms.uAlpha.value = videoAlpha;
      matRef.current.uniforms.uRadius.value = radius;
    }

    return () => {
      v1.pause(); v1.src = "";
      t1.dispose();

      if (v2) { v2.pause(); v2.src = ""; }
      if (t2) t2.dispose();
    };
  }, [src, loopSrc, gl, radius]);

  useEffect(() => {
    if (matRef.current) matRef.current.uniforms.uAlpha.value = videoAlpha;
  }, [videoAlpha]);

  // swap a nubes cuando esté listo (sin negro)
  const switchToLoop = () => {
    const v2 = loopVideoRef.current;
    const t2 = loopTexRef.current;
    if (!v2 || !t2 || !matRef.current) return;

    // esperar a que haya frame disponible
    const onCanPlay = () => {
      // swap textura
      matRef.current.uniforms.uTexture.value = t2;
      // arrancar nubes
      v2.play().catch(() => {});
      v2.removeEventListener("canplay", onCanPlay);
    };

    // si ya está listo, switchea ya
    if (v2.readyState >= 3) {
      onCanPlay();
    } else {
      v2.addEventListener("canplay", onCanPlay);
      // fuerza carga
      v2.load();
    }
  };

  useImperativeHandle(ref, () => ({
    setAlpha: (a) => {
      if (matRef.current) matRef.current.uniforms.uAlpha.value = a;
    },
    play: () => {
      const v1 = mainVideoRef.current;
      if (!v1) return;

      // arrancar audio loop con fade-in
      audioRef.current?.play?.();

      // cuando termina despegue -> switchear a nubes
      const onEnded = () => {
        v1.removeEventListener("ended", onEnded);
        switchToLoop();
      };
      v1.addEventListener("ended", onEnded);

      v1.play().catch(() => {});
    },
    pause: () => {
      mainVideoRef.current?.pause?.();
      audioRef.current?.pause?.();
    },
    getVideo: () => mainVideoRef.current,
  }), []);

  // uniforms iniciales (se setean en el effect)
  const uniforms = useMemo(() => ({
    uTexture: { value: null },
    uAlpha: { value: videoAlpha },
    uRadius: { value: radius },
  }), [radius]);

  return (

    <>

      <AudioLooper
        ref={audioRef}
        url={INTERIOR_RESOURCES.SonidoHelicoptero.url}
        fadeIn={2.0}
        fadeOut={1.5}
        volume={1}
        loopStart={0.05}
        loopEndTrim={0.05}
      />
    
      <mesh 
      frustumCulled={false}
      renderOrder={-1000}
      rotation={rotation}>
        <sphereGeometry args={[
          radius,
          128, // segmentos horizontales
          96,  // segmentos verticales
          0,           // phiStart - inicio horizontal
          Math.PI * 2, // phiLength - 360° horizontal completo
          Math.PI * 0.25, // thetaStart - inicio vertical (25% desde arriba = saca 1/4 superior)
          Math.PI * 0.75  // thetaLength - rango vertical (75% del total = medio + abajo)
        ]} />
        <shaderMaterial
          ref={matRef}
          vertexShader={staticVertex}
          fragmentShader={videoFragment}
          uniforms={uniforms}
          side={THREE.BackSide}
          transparent
          depthWrite={false}
          depthTest={false}
          />
      </mesh>
    </>
  );
});

export default VideoSphere;
